import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type PropertyDetail = components['schemas']['PropertyDetailResponse'];
export type PropertyEdits = components['schemas']['UpdatePropertyRequest'];

/**
 * `latitude`/`longitude` round-trip through Postgres `numeric` as a string as
 * often as a number — every reader of a property's coordinates (the Location
 * block, the job screen's mini-map) needs this, not just one of them.
 */
export function toNumericCoordinate(value: number | string | null | undefined): number | null {
  const n = typeof value === 'string' ? Number(value) : value;
  return n == null || Number.isNaN(n) ? null : n;
}

/**
 * The property detail page's data (M1-05): the property itself, plus its own
 * recent jobs — bounded server-side to the 20 most recently created
 * (api/CONVENTIONS.md), so a Technician who can view this property at all
 * (see the API's ownership check) sees "past jobs at this property" without
 * needing `jobs.read.all`.
 *
 * A 403 is a real, distinguishable answer here too — a Technician opening a
 * property they have no job at — surfaced as `PropertyNotPermittedError` the
 * same way `JobNotPermittedError` does for M1-13's job page.
 */
export function useProperty(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['properties', propertyId],
    enabled: propertyId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/properties/{id}', {
        params: { path: { id: propertyId! } },
      });
      if (response.status === 403) {
        throw new PropertyNotPermittedError();
      }
      if (response.status === 404) {
        throw new PropertyNotFoundError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/properties/{id} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export class PropertyNotFoundError extends Error {
  constructor() {
    super('Property not found');
    this.name = 'PropertyNotFoundError';
  }
}

export class PropertyNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view this property');
    this.name = 'PropertyNotPermittedError';
  }
}

/**
 * Edit a property, from its detail page (M1-05's scope extension — M1-02
 * shipped `PUT /properties/{id}` with no caller). `CustomerId` stays absent
 * from the form, matching the API: reassigning a property to a different
 * customer would need the party links to follow it, its own migration story.
 */
export function useUpdateProperty(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (edits: PropertyEdits) => {
      const { data, response } = await apiClient.PUT('/api/v1/properties/{id}', {
        params: { path: { id: propertyId } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/properties/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      // The customer page's property cards read this list too.
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer'] });
    },
  });
}

/**
 * Archive a property, from its detail page. Idempotent on the API side
 * (M1-02) — archiving an already-archived property just confirms it — same
 * reasoning as `useArchiveCustomer`.
 */
export function useArchiveProperty(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, response } = await apiClient.POST('/api/v1/properties/{id}/archive', {
        params: { path: { id: propertyId } },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/properties/{id}/archive failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer'] });
    },
  });
}

/**
 * Delete a property outright, from its detail page. A hard delete, and the
 * API blocks it once the property has job history (M1-02) — the AC draws the
 * line at archive as the normal action, delete as the exception, and asks
 * that the API's refusal be surfaced rather than the button hidden. The 409
 * body is the refusal message itself (`Conflict<string>` on the API side, not
 * ProblemDetails), so it comes back on `error`, not `data` — openapi-fetch
 * only populates `data` for a 2xx response.
 */
export function useDeleteProperty(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { response, error } = await apiClient.DELETE('/api/v1/properties/{id}', {
        params: { path: { id: propertyId } },
      });
      if (response.status === 409) {
        throw new PropertyHasJobHistoryError(error ?? '');
      }
      if (!response.ok) {
        throw new Error(`DELETE /api/v1/properties/{id} failed: ${response.status}`);
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['properties', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer'] });
    },
  });
}

/**
 * Re-run geocoding for a property (M1-23) — the retry the AC asks for when a
 * save's lookup failed or found nothing. Returns the refreshed record so the
 * caller can announce the NEW status, which is not necessarily Geocoded: a
 * retry against an address that does not exist is honestly NoMatch again.
 */
export function useGeocodeProperty(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, response } = await apiClient.POST('/api/v1/properties/{id}/geocode', {
        params: { path: { id: propertyId } },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/properties/{id}/geocode failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer'] });
    },
  });
}

/**
 * Drags the marker to correct a pin (M1-26) — a rooftop match that misses a
 * long rural driveway. Only reachable for a property that already HAS a pin,
 * since that is the only case `PropertyLocationMap` is rendered for; placing
 * one on a `NoMatch` is not something the app offers. The API flags the row
 * `manuallyAdjusted` so a later address edit does not silently re-geocode over
 * it; only an explicit `useGeocodeProperty` retry wins it back, which
 * `PropertyDetail` keeps offering for exactly that reason.
 */
export function useSetPropertyLocation(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (location: { latitude: number; longitude: number }) => {
      const { data, response } = await apiClient.PUT('/api/v1/properties/{id}/location', {
        params: { path: { id: propertyId } },
        body: location,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/properties/{id}/location failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer'] });
    },
  });
}

/**
 * Thrown when the API asked and this ADDRESS has nothing locatable in it — not
 * the town, not the ZIP, not even the state.
 *
 * ⚠️ Deliberately NOT thrown when the provider could not be asked. Both are a
 * 503 with the same title, so only the problem body's `reason` tells them apart,
 * and the screen has to: one sends the office to check the city, state and ZIP,
 * the other asks them to try again in a moment. Sending somebody to correct a
 * perfectly good address because Azure Maps was throttling — or because the
 * environment has no Maps account at all — is the failure this split exists to
 * prevent.
 */
export class LocationHintUnavailableError extends Error {
  constructor() {
    super('No starting point for the map');
    this.name = 'LocationHintUnavailableError';
  }
}

/** The `reason` the API stamps on the 503 that is about the address. */
const ADDRESS_NOT_LOCATABLE = 'AddressNotLocatable';

/**
 * Where to open the map for a property with no pin (M1-36).
 *
 * ⚠️ `enabled` is false by default and the caller turns it on, because this
 * costs a geocode call at the provider. It runs when the office asks to place a
 * pin, never on a property read — hanging one off every page load would buy an
 * answer almost nobody needs.
 *
 * `staleTime: Infinity`: the centre of a town does not move, and the office
 * nudging a pin around should not re-ask for it.
 */
export function usePropertyLocationHint(propertyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['properties', propertyId, 'location-hint'] as const,
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET(
        '/api/v1/properties/{id}/location-hint',
        { params: { path: { id: propertyId } } },
      );
      // ⚠️ Only the address-level 503 gets the wording that blames the address.
      // A provider that could not be asked falls through to the generic error
      // below, which says "try again in a moment" instead. `reason` is a
      // ProblemDetails extension, so it is not on the generated type.
      const reason = (error as { reason?: string } | undefined)?.reason;
      if (response.status === 503 && reason === ADDRESS_NOT_LOCATABLE) {
        throw new LocationHintUnavailableError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/properties/{id}/location-hint failed: ${response.status}`);
      }
      return data;
    },
  });
}

export class PropertyHasJobHistoryError extends Error {
  constructor(apiMessage: string) {
    super(apiMessage);
    this.name = 'PropertyHasJobHistoryError';
  }
}

/**
 * The property's FULL job history, for a caller who holds `jobs.read.all` —
 * office staff reviewing everything, as opposed to `PropertyDetailResponse`'s
 * embedded, bounded `recentJobs`, which is what a Technician-only caller sees
 * instead. `enabled` carries the permission the same way `useCustomerJobs`
 * does: a caller without it never makes the request at all, rather than
 * making it and reading a 403 into an error box.
 */
export function usePropertyJobs(propertyId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['jobs', 'byProperty', propertyId],
    enabled: propertyId !== undefined && enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs', {
        params: { query: { propertyId: propertyId!, pageSize: 100 } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs failed: ${response.status}`);
      }
      return data.items;
    },
  });
}
